import { useState } from 'react';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQCategory {
  category: string;
  items: FAQItem[];
}

interface FAQAccordionProps {
  categories: FAQCategory[];
}

function FAQItem({ item, index, isOpen, onToggle }: {
  item: FAQItem;
  index: number;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div
      style={{
        borderTop: isOpen
          ? '1px solid rgba(211,179,102,0.35)'
          : '1px solid rgba(6,30,69,0.1)',
        transition: 'border-color 300ms ease',
      }}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-start gap-6 md:gap-8 py-7 text-left cursor-pointer group"
      >
        {/* Number */}
        <span
          className="text-5xl font-light leading-none shrink-0 transition-colors duration-200 self-center"
          style={{
            fontFamily: "'Playfair Display', serif",
            color: isOpen ? '#d3b366' : 'rgba(6,30,69,0.18)',
            lineHeight: '1',
          }}
        >
          {String(index + 1).padStart(2, '0')}
        </span>

        {/* Question */}
        <span
          className="flex-1 text-[13px] uppercase tracking-[0.1em] leading-snug transition-colors duration-200"
          style={{
            fontFamily: "'Google Sans Flex', sans-serif",
            fontWeight: 500,
            color: isOpen ? '#061e45' : 'rgba(6,30,69,0.6)',
          }}
        >
          {item.question}
        </span>

        {/* Toggle icon */}
        <div
          className="shrink-0 w-8 h-8 md:w-9 md:h-9 flex items-center justify-center self-center transition-all duration-300"
          style={{
            border: isOpen
              ? '1px solid rgba(211,179,102,0.5)'
              : '1px solid rgba(6,30,69,0.12)',
            background: isOpen ? 'rgba(211,179,102,0.08)' : 'transparent',
          }}
        >
          <i
            className="ri-add-line text-sm transition-all duration-300"
            style={{
              color: isOpen ? '#d3b366' : 'rgba(6,30,69,0.35)',
              transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
              display: 'inline-block',
            }}
          />
        </div>
      </button>

      {/* Answer */}
      <div
        className="overflow-hidden transition-all duration-500 ease-in-out"
        style={{ maxHeight: isOpen ? '400px' : '0px', opacity: isOpen ? 1 : 0 }}
      >
        <div className="flex gap-6 md:gap-8 pb-7">
          <div className="shrink-0 w-[calc(3rem+1.5rem)] md:w-[calc(3rem+2rem)]" />
          <p
            className="text-[15px] leading-relaxed"
            style={{
              fontFamily: "'Google Sans Flex', sans-serif",
              fontWeight: 400,
              color: 'rgba(6,30,69,0.5)',
            }}
          >
            {item.answer}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function FAQAccordion({ categories }: FAQAccordionProps) {
  const [openMap, setOpenMap] = useState<Record<string, number | null>>({});

  const toggle = (catKey: string, idx: number) => {
    setOpenMap(prev => ({
      ...prev,
      [catKey]: prev[catKey] === idx ? null : idx,
    }));
  };

  return (
    <section className="py-24 px-5 md:px-[75px]">
      <div className="max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-16 gap-y-16">
          {categories.map((cat) => (
            <div key={cat.category}>
              {/* Category header */}
              <div className="flex items-center gap-5 mb-10">
                <div
                  className="w-1 h-8 shrink-0"
                  style={{ background: 'linear-gradient(to bottom, #d3b366, #a8894a)' }}
                />
                <h2
                  className="text-[#061e45] text-xl md:text-2xl font-light"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {cat.category}
                </h2>
                <div className="flex-1 h-px" style={{ background: 'rgba(6,30,69,0.08)' }} />
                <span
                  className="text-[11px] uppercase tracking-[0.18em] shrink-0"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif", color: 'rgba(6,30,69,0.3)' }}
                >
                  {cat.items.length} questions
                </span>
              </div>

              {/* Items */}
              <div>
                {cat.items.map((item, idx) => (
                  <FAQItem
                    key={idx}
                    item={item}
                    index={idx}
                    isOpen={openMap[cat.category] === idx}
                    onToggle={() => toggle(cat.category, idx)}
                  />
                ))}
                {/* Bottom border on last item */}
                <div style={{ borderBottom: '1px solid rgba(6,30,69,0.1)' }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}